import { useParams, useSearchParams } from "react-router-dom";
import { ClientBooking } from "@/components/pages/Client";
import { ClientScopeProvider } from "@/components/pages/Client/settings/ClientScopeContext";
import { useHydrateBookingFromQuote } from "@/components/pages/Guest/useHydrateBookingFromQuote";
import { OperatorBookingBanner } from "./OperatorBookingBanner";

/**
 * Operator books a trip on behalf of a client (DEV-462). Hosts the existing client booking
 * sheet under the operator shell, scoped to `:clientId` via ClientScopeProvider so every step
 * (Address, Trip Details, Payment) and the live quote read/write the client's account.
 *
 * The sheet is a full-bleed map surface — not a Manage-Client tab — so it mounts as its own
 * route (sibling to details/favourites/settings). `-mx` cancels the operator `<main>` padding
 * for full bleed and the fixed height (viewport minus the 65px operator header) gives the map
 * a definite box to fill, matching the client-portal layout it normally relies on.
 */
export const OperatorClientBooking = () => {
  const { clientId = "" } = useParams<{ clientId: string }>();
  const [searchParams] = useSearchParams();

  // Widget hand-off: an operator arriving from a logged-in widget quote (AuthedWidgetRedirect) carries a
  // `quote_uuid`. Prefill the draft from it. The operator is already fixed by the `:operatorId` route, so
  // there's nothing to resolve here (resolveOperator: false). No `quote_uuid` → no-op (normal OBO booking).
  const quoteUuid = searchParams.get("quote_uuid") ?? undefined;
  useHydrateBookingFromQuote({ quoteUuid, resolveOperator: false });

  return (
    <ClientScopeProvider accountId={clientId}>
      <div className="-mx-3 flex h-[calc(100vh-65px)] flex-col lg:-mx-5">
        <OperatorBookingBanner accountId={clientId} />
        <ClientBooking />
      </div>
    </ClientScopeProvider>
  );
};
